import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Summary } from '../types';
import { formatTimestampToDay } from 'support/helpers/dateTime/dateTime';

type ShippingNoticeSummaryValues = {
  totalQuantity: number;
  lineItemCount: number;
  deliveryDate?: number;
};

const calculateLineItemsQuantity = (lineItems: Array<any> | undefined): number => {
  return (
    lineItems?.reduce((acc, item) => {
      if (item.line_items?.length) {
        return acc + calculateLineItemsQuantity(item.line_items);
      }

      return acc + (item.shipped_quantity ?? 0);
    }, 0) || 0
  );
};

type ShippingNoticeLineItem = {
  shipped_quantity: number;
};

export const useShippingNoticeSummaryValues = (): ShippingNoticeSummaryValues => {
  const [lineItems, expectedDeliveryDate] = useWatch({
    name: ['line_items', 'info.0.expected_delivery_date'],
  });

  const confirmedLineItems = lineItems?.filter((lineItem: ShippingNoticeLineItem) => lineItem.shipped_quantity > 0);
  return useMemo(
    () => ({
      totalQuantity: calculateLineItemsQuantity(lineItems),
      lineItemCount: confirmedLineItems?.length ?? 0,
      deliveryDate: expectedDeliveryDate,
    }),
    [lineItems, confirmedLineItems?.length, expectedDeliveryDate],
  );
};

export const useShippingNoticeSummary = (): Summary => {
  const { t } = useTranslation();
  const values = useShippingNoticeSummaryValues();

  return useMemo(() => {
    return {
      content: [
        [
          {
            label: t('webedi:documentSummary.shippingNotice.shippedLineItemCount.label'),
            value: Math.round(values.lineItemCount),
          },
          {
            label: t('webedi:documentSummary.shippingNotice.totalQuantity.label'),
            value: Math.round(values.totalQuantity),
          },
          {
            label: t('webedi:documentSummary.shippingNotice.deliveryDate.label'),
            value: formatTimestampToDay(values.deliveryDate) ?? '—',
          },
        ],
      ],
    };
  }, [t, values]);
};
